import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzIcon, BringzzText } from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { alphabets } from './constants';

const CategoryProducts = () => {
    const { t } = useTranslation();
    const { categoryUUID } = useParams();
    const { navigate, backPress } = useNavigation();
    const { sendRequest } = useRequest();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alphabetFilter, setAlphabetFilter] = useState('#');

    // Extract category name from UUID (format: category_Name_uuid)
    const getCategoryName = () => {
        if (!categoryUUID) return '';
        const parts = categoryUUID.split('_');
        if (parts.length >= 2) {
            return decodeURIComponent(parts.slice(1, -1).join(' '));
        }
        return '';
    };

    const categoryName = getCategoryName();

    useEffect(() => {
        if (!categoryUUID) return;

        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await sendRequest({
                    method: 'GET',
                    url: `/module/getProductsByCategory/productcategory?categoryUUID=${encodeURIComponent(categoryUUID)}`,
                });

                if (response?.status === 'success' && response?.data) {
                    setProducts(response.data);
                }
            } catch (error) {
                console.error('Error fetching category products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [categoryUUID]);

    const handleProductClick = (product) => {
        navigate(`/product/details/${product.productUUID}`);
    };

    const filteredProducts = products.filter((product) => {
        if (alphabetFilter === '#' || alphabetFilter === 'All') {
            return true;
        }
        return product.name?.toUpperCase().startsWith(alphabetFilter);
    });

    return (
        <BringzzPageContainer
            className="bg-sand pb-32"
            authenticated={false}
            footerHover={true}
            header={{
                left: <BringzzIcon
                    size="22"
                    icon="IconChevronLeft"
                    folder="LineIcons"
                    className="cursor-pointer"
                    onClick={backPress}
                />,
                title: categoryName
            }}
        >
            <div className='pt-2'>
                <div className='rounded-xl bg-white mx-2 py-2'>
                    {/* Alphabet Filter */}
                    <div className='flex items-center overflow-x-auto sticky top-0 bg-white z-10 py-2 border-b' style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                        <BringzzText tag='h3' className='whitespace-nowrap mr-1'>A-Z</BringzzText>
                        <div className='flex space-x-3 sm:space-x-2 min-w-0'>
                            {alphabets.map((char, index) => {
                                const isSelected = char === alphabetFilter;
                                return (
                                    <div
                                        key={index}
                                        className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                            isSelected && "!bg-magic-lilac"
                                        )}
                                        onClick={() => setAlphabetFilter(char)}
                                    >
                                        <BringzzText tag='h5'>{char}</BringzzText>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : filteredProducts.length > 0 ? (
                        filteredProducts.map((product, index) => (
                            <div
                                key={product.productUUID || index}
                                className={classNames(
                                    'p-4 flex items-center justify-between cursor-pointer',
                                    index !== filteredProducts.length - 1 && 'border-b',
                                    'hover:bg-magic-lilac/10 transition-colors duration-300'
                                )}
                                onClick={() => handleProductClick(product)}
                            >
                                <div className='flex-grow min-w-0'>
                                    <BringzzText tag='h4' className="truncate">
                                        {product.name}
                                    </BringzzText>
                                    <BringzzText tag='p' className='text-sm text-gray-500 truncate'>
                                        {product.companyname}
                                    </BringzzText>
                                </div>
                                <BringzzIcon
                                    size="16"
                                    icon="IconChevronRight"
                                    folder="LineIcons"
                                    className="text-gray-400 flex-shrink-0 ml-2"
                                />
                            </div>
                        ))
                    ) : (
                        <div className="p-4 text-center">
                            <BringzzText tag='p'>
                                {t('component.search.empty.no_products') || 'No products found'}
                            </BringzzText>
                        </div>
                    )}
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default CategoryProducts;
